<template>
  <div id="navigation">
    <router-link to="/">
      <v-btn
        plain
        height="56"
        v-ripple="{ center: true }"
        style="opacity: 1"
        class="px-2"
        color="secondary"
        :fab="$vuetify.breakpoint.xs"
      >
        <logo-icon-svg
          :color="$vuetify.theme.currentTheme.secondary"
          class="logo"
          :class="{ 'mr-2': !$vuetify.breakpoint.xs }"
        ></logo-icon-svg>
        <!-- Website name (Desktop only) -->
        <logo-text-svg
          v-if="!$vuetify.breakpoint.xs"
          :color="$vuetify.theme.currentTheme.secondary"
          class="logo-text"
        ></logo-text-svg>
      </v-btn>
    </router-link>

    <v-spacer></v-spacer>

    <!-- Explore Menu-->
    <v-menu offset-y class="explore-menu">
      <template #activator="{ on, attrs }">
        <v-btn color="background" elevation="0" v-bind="attrs" v-on="on" class="explore-menu-button">
          Explore <v-icon>mdi-menu-down</v-icon>
        </v-btn>
      </template>

      <v-list class="explore-menu-options" active-class="v-list-item--active" color="background">
        <v-list-item v-for="medium in Object.values($mediums)" :key="medium" :to="{ path: `/explore/${medium}` }">
          {{ userFriendlyMediumWord(medium) }}
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Search -->
    <div id="search" class="mr-4">
      <template>
        <v-btn :to="{ name: 'search' }" fab small elevation="0" active-class="no-active" color="background">
          <v-icon medium> mdi-magnify </v-icon>
        </v-btn>
      </template>
    </div>

    <!-- User Avatar -->

    <!-- Check For Login -->
    <template v-if="username">
      <!-- Profile Button -->
      <v-btn id="profile-button" @click.stop="menuDrawerActive = true" :ripple="false" icon x-large color="secondary">
        <v-badge dot bottom overlap :value="notifications > 0" color="primary">
          <profile-avatar :size="40" />
        </v-badge>
      </v-btn>

      <!-- Profile Menu for Desktop Users -->
      <v-menu
        id="desktop-profile-menu"
        v-if="!$vuetify.breakpoint.xs"
        v-model="menuDrawerActive"
        attach="v-app"
        transition="slide-x-reverse-transition"
        flat
      >
        <v-card tile class="pa-4 pt-2" color="background">
          <!-- Header Menu Items (Profile & Settings) -->
          <v-list tile nav color="background">
            <v-list-item>
              <v-list-item link v-bind:to="{ path: '/profile/' + username }" active-class="no-active">
                <profile-avatar :size="64" />

                <v-list-item-content class="pl-1">
                  <v-list-item-title :key="profile.firstName" v-if="profile.firstName"
                    >{{ profile.firstName }} {{ profile.lastName }}</v-list-item-title
                  >
                  <v-list-item-title v-else>{{ username }}</v-list-item-title>

                  <v-list-item-subtitle>@{{ username.toLowerCase() }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-list-item-action>
                <v-btn icon v-bind:to="{ name: 'settings' }" active-class="no-active">
                  <v-icon small> mdi-cog </v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <v-divider></v-divider>

          <v-list nav tile color="background">
            <v-list-item-group dense>
              <v-list-item
                v-for="item in desktopProfileItems"
                :key="item.title"
                v-bind:to="item.to"
                @click="invokeMethod(item.click)"
                active-class="no-active"
              >
                <v-list-item-icon>
                  <v-badge dot bottom :value="item.notification === true && notifications > 0" color="primary">
                    <v-icon> {{ item.icon }} </v-icon>
                  </v-badge>
                </v-list-item-icon>

                <v-list-item-title> {{ item.title }} </v-list-item-title>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card>
      </v-menu>

      <!-- Profile Menu for Mobile Users -->
      <v-navigation-drawer
        id="mobile-profile-menu"
        v-else
        v-model="menuDrawerActive"
        color="background"
        right
        app
        temporary
      >
        <v-list tile nav dense>
          <v-list-item link v-bind:to="{ path: '/profile/' + username }" active-class="no-active">
            <profile-avatar :size="64" />

            <v-list-item-content class="pl-1">
              <v-list-item-title v-if="profile.firstName"
                >{{ profile.firstName }} {{ profile.lastName }}</v-list-item-title
              >
              <v-list-item-title v-else>{{ username }}</v-list-item-title>

              <v-list-item-subtitle>@{{ username.toLowerCase() }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <v-divider></v-divider>

        <v-list>
          <v-list-item-group dense>
            <v-list-item
              v-for="item in mobileProfileItems"
              :key="item.title"
              v-bind:to="item.to"
              @click="item.click ? invokeMethod(item.click) : closeProfileDrawer()"
              active-class="no-active"
            >
              <v-list-item-icon>
                <v-badge dot bottom :value="item.notification === true && notifications > 0" color="primary">
                  <v-icon> {{ item.icon }} </v-icon>
                </v-badge>
              </v-list-item-icon>

              <v-list-item-title> {{ item.title }} </v-list-item-title>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-navigation-drawer>
    </template>

    <!-- Login Prompt -->
    <template v-else>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            id="profile-button"
            class="mr-1"
            @click.stop="menuDrawerActive = true"
            icon
            x-large
            color="secondary"
          >
            <v-icon class="mr-1"> mdi-login </v-icon>
          </v-btn>
        </template>

        <span>Login</span>
      </v-tooltip>

      <v-menu
        id="desktop-login-menu"
        :close-on-content-click="false"
        v-model="menuDrawerActive"
        attach="v-app"
        transition="slide-x-reverse-transition"
        flat
      >
        <v-card tile color="background">
          <v-card-title>Sign In</v-card-title>

          <v-card-subtitle style="padding-bottom: 8px">
            <router-link :to="'/create-account'" class="anchor--text"> Don't have an account? Sign Up! </router-link>
          </v-card-subtitle>

          <v-list tile nav color="background">
            <v-list-item>
              <v-text-field
                v-model="loginInfo.username"
                type="text"
                name="username"
                placeholder="Username"
                class="prepended-icon"
                style="opacity: 0.75"
                hide-details
                filled
                rounded
                dense
                prepend-inner-icon="mdi-account"
                color="primaryText"
              />
            </v-list-item>

            <v-list-item>
              <v-text-field
                v-model="loginInfo.password"
                type="password"
                name="password"
                placeholder="Password"
                @keyup.enter="login()"
                class="prepended-icon"
                style="opacity: 0.75"
                hide-details
                filled
                rounded
                dense
                prepend-inner-icon="mdi-lock"
                color="primaryText"
              />
            </v-list-item>

            <p class="forgot-password">
              <router-link :to="{ name: 'forgot-password' }">Forgot password?</router-link>
            </p>

            <div class="text-center red--text">{{ loginInfo.error }}</div>

            <v-checkbox
              v-model="loginInfo.rememberMe"
              label="Remember me"
              class="my-0 mb-3 pt-0 pl-3"
              hide-details
              dense
              color="primaryText"
            ></v-checkbox>

            <v-list-item class="mt4">
              <v-btn v-on:click="login()" block rounded color="primary" elevation="0">Login</v-btn>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
    </template>
  </div>
</template>

<script>
import { setCookie } from "../util/cookieTools";
import sessionTools from "../mixins/sessionTools";
import { mapMutations } from "vuex";
import isMobileBuild from "../util/isMobileBuild";
import { Preferences } from "@capacitor/preferences";
import { userFriendlyMediumWord } from "@/util/mediumHelpers";
import logoIconSvg from "./logo-svg";
import logoTextSvg from "./logo-text-svg.vue";

const NOTIFICATION_POLLING_INTERVAL_MS = 30 * 1000;

export default {
  name: "top-navbar",

  mixins: [sessionTools],

  components: { logoIconSvg, logoTextSvg },

  data: () => ({
    profile: {
      username: null,
      firstName: null,
      lastName: null,
    },

    loginInfo: {
      username: null,
      password: null,
      rememberMe: false,
      error: null,
    },

    // Profile Drawer Variables
    notificationCount: 0,
    menuDrawerActive: false,

    sideNavExpanded: false,
  }),

  computed: {
    username() {
      return this.$store.state.username;
    },
    notifications() {
      return this.$store.state.notifications;
    },

    desktopProfileItems() {
      return [
        {
          title: "Notifications",
          icon: "mdi-bell",
          to: { name: "notifications" },
          notification: true,
        },
        {
          title: "Saved for Later",
          icon: "mdi-bookmark",
          to: { name: "saved-for-later", params: { username: this.username } },
        },
        { title: "Logout", icon: "mdi-logout", click: "navbarLogout" },
      ];
    },
    mobileProfileItems() {
      return [
        {
          title: "Notifications",
          icon: "mdi-bell",
          to: { name: "notifications" },
          notification: true,
        },
        {
          title: "Saved for Later",
          icon: "mdi-bookmark",
          to: { name: "saved-for-later", params: { username: this.username } },
        },
        { title: "Settings", icon: "mdi-cog", to: { name: "settings" } },
        { title: "Logout", icon: "mdi-logout", click: "navbarLogout" },
      ];
    },
  },

  watch: {
    username(newUserName) {
      this.loginInfo.username = null;
      this.loginInfo.password = null;
      this.loginInfo.rememberMe = false;
      if (newUserName === "") {
        this.$store.commit("setNotifications", { count: 0 });
      } else {
        this.getNotifications();
      }
    },

    // On page change, close login drawer
    $route() {
      this.menuDrawerActive = false;
    },
  },

  mounted() {
    this.getNotifications();
    setInterval(this.getNotifications, NOTIFICATION_POLLING_INTERVAL_MS);
  },

  methods: {
    ...mapMutations(["showSnackbar"]),
    userFriendlyMediumWord,

    invokeMethod(methodName) {
      if (this[methodName] !== undefined) this[methodName]();
    },

    // closeMenuDrawer() { this.menuDrawerActive = false; },

    getNotifications() {
      if (!this.username) {
        return;
      }
      this.$axios
        .get("/v1/notifications", {
          params: {
            read: false,
            limit: 1,
          },
        })
        .then((response) => {
          this.$store.commit("setNotifications", { count: response.data.items.length });
        });
    },

    login: function () {
      let loginAuth = {
        password: this.loginInfo.password,
      };
      if (this.loginInfo.username.includes("@")) loginAuth.email = this.loginInfo.username;
      else loginAuth.username = this.loginInfo.username;

      if (this.loginInfo.username !== "" && this.loginInfo.password !== "") {
        this.$axios
          .post("/login", loginAuth)
          .then(async (response) => {
            let jsonToken = response.data;
            let expirationDays = null;
            if (this.loginInfo.rememberMe) expirationDays = 3650;
            setCookie("authorization", jsonToken, expirationDays);
            this.$store.commit("login", { authorization: jsonToken });
            this.$axios.defaults.headers.common["Authorization"] = `Bearer ${jsonToken}`;
            this.$router.push({ name: "home" }); // routes user to homepage from wherever they are. Is it needed?
            this.menuDrawerActive = false;

            if (isMobileBuild()) {
              await Preferences.set({
                key: "authorization",
                value: jsonToken,
              });
            }
          })
          .catch((err) => {
            if (err.response && err.response.status === 422) {
              this.loginInfo.error = "Username or Password is incorrect";
            } else {
              this.showSnackbar({
                error: true,
                text: "An unexpected error occurred!",
              });
            }
          });
      } else this.loginInfo.error = "Username or Password empty";
    },

    /*
      @description Log out the current user and redirect to the home page.
    */
    navbarLogout() {
      // A glitch occurs where the profile drawer is open if you log out then back in again.
      this.menuDrawerActive = false;
      this.profile = {
        username: null,
        firstName: null,
        lastName: null,
      };

      this.logout();
    },
  },
};
</script>

<style>
:root {
  --logo-height: 28px;
  --logo-icon-width: calc(var(--logo-height) / 0.70625);
}
</style>

<style scoped>
#navigation {
  position: fixed;
  z-index: 9;

  width: 100%;
  background-color: var(--v-background-base);
  border-bottom: 1px solid var(--v-divider-base);
  padding: 4px 16px 4px 16px;

  /* For mobile friendliness */
  padding-top: max(env(safe-area-inset-top, 4px), 4px);

  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute !important;
  right: 0;
  top: 0;
}

>>> .v-btn__content {
  opacity: 1 !important;
}

.logo {
  height: var(--logo-height);
  width: var(--logo-icon-width);
}

.logo-text {
  height: var(--logo-height);
  min-width: 175px;
}

*[id$="-menu"] {
  width: 320px;
  max-width: 80vw;

  display: initial !important;

  position: absolute;
  right: 0 !important;
  top: 100% !important;

  border-bottom-left-radius: 4px 4px;
}

*[id$="-menu"] > .v-menu__content {
  width: 100%;

  border-radius: 0;

  box-shadow:
    0 3px 5px -3px rgba(0, 0, 0, 0.2),
    0 1px 6px 1px rgba(0, 0, 0, 0.12);
}

.no-active::before {
  opacity: 0 !important;
}
.no-active:hover::before {
  opacity: 0.08 !important;
}

/* Handles prepended icons, and spacing them correctly in roudned text input */
.v-text-field.prepended-icon >>> .v-input__slot {
  padding: 0;
}
.v-text-field.prepended-icon >>> .v-input__slot .v-input__prepend-inner {
  margin-left: 10px;
  margin-right: 8px;
  padding: 0;
}

/* Avatar margins */
#mobile-profile-menu .profile-avatar,
#desktop-profile-menu .profile-avatar {
  margin: 8px 16px 8px 0;
}

.forgot-password {
  font-size: 0.875em;
  text-align: center;
}

.explore-menu-button {
  text-transform: none;
}

.theme--light {
  .explore-menu-options a.v-list-item--active {
    background-color: var(--v-background-darken1);
  }

  .explore-menu-options a:hover {
    background-color: var(--v-background-darken2);
  }
}

.theme--dark {
  .explore-menu-options a.v-list-item--active {
    background-color: var(--v-background-lighten1);
  }

  .explore-menu-options a:hover {
    background-color: var(--v-background-lighten2);
  }
}
</style>
